import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { ProductChange } from "../models/ProductChange";
import { PRODUCT_CHANGE_INITIAL_VALUES } from "../models/ProductChange";
import { useProductChanges } from "../hooks/useProductChanges";
import {
	useDeleteProductChange,
	useRecalculateProductChange,
	useUpsertProductChange,
} from "../hooks/useProductChangeMutation";
import { ProductChangesTable } from "./ProductChangesTable";
import { ProductChangeDetailViewer } from "./ProductChangeDetailViewer";
import { ProductChangesToolbar } from "./ProductChangesToolbar";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	productChangeId: urlParam.int(),
};

export const ProductChangesManager = () => {
	// Filters and the selected record, all in the query string.
	const [{ search, productChangeId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [changeToDelete, setChangeToDelete] = useState<ProductChange | null>(
		null,
	);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "product-changes-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { productChanges, isLoading, error: queryError } = useProductChanges();

	const filteredChanges = useMemo(() => {
		if (!searchText) return productChanges;
		const s = searchText.toLowerCase();
		return productChanges.filter(
			(c) =>
				c.InitialProductSizeName?.toLowerCase().includes(s) ||
				c.FinalProductSizeName?.toLowerCase().includes(s),
		);
	}, [productChanges, searchText]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<ProductChange>({
		id: productChangeId,
		setId: (value) => setFilters({ productChangeId: value }),
		dropId: () => dropValues("productChangeId"),
		rows: productChanges,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedChange = selection.current;

	const { upsertProductChange, isUpserting } = useUpsertProductChange({
		onSuccess: (data) => {
			const isNew = selection.isNew;
			setNotification(
				isNew
					? "Product change created successfully"
					: "Product change updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteProductChange, isDeleting } = useDeleteProductChange({
		onSuccess: () => {
			setNotification("Product change deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setChangeToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const { recalculate, isRecalculating } = useRecalculateProductChange({
		onSuccess: (data) => {
			setNotification(
				`Recalculated: ${data.NumberOfChanges ?? 0} changes, avg time: ${data.AverageChangeTime?.toFixed(2) ?? "N/A"} min`,
			);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(change: ProductChange | null) => {
			if (change) selection.select(change);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((change: ProductChange) => {
		setChangeToDelete(change);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (changeToDelete?.Id) {
			deleteProductChange(changeToDelete.Id);
		}
	}, [changeToDelete, deleteProductChange]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setChangeToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...PRODUCT_CHANGE_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(change: ProductChange) => {
			upsertProductChange(change);
		},
		[upsertProductChange],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	const handleRecalculate = useCallback(
		(id: number) => {
			recalculate(id);
		},
		[recalculate],
	);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
				Product Changes
			</Typography>

			<ProductChangesToolbar
				search={searchText}
				onSearchChange={setSearchText}
				onAdd={handleAdd}
			/>

			<Group
				id="product-changes-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={1}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<ProductChangesTable
									productChanges={filteredChanges}
									selectedChange={selectedChange?.Id ? selectedChange : null}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={1}
							sx={{
								height: "100%",
								overflowY: "scroll",
								overflowX: "hidden",
							}}
						>
							<ProductChangeDetailViewer
								change={selectedChange}
								isSaving={isUpserting || isDeleting}
								isRecalculating={isRecalculating}
								onSave={handleSave}
								onClose={handleClose}
								onRecalculate={handleRecalculate}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE PRODUCT CHANGE"
				itemName={
					changeToDelete
						? `${changeToDelete.InitialProductSizeName} → ${changeToDelete.FinalProductSizeName}`
						: ""
				}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
